import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "./components/GridBackground";
import { FlowArrow } from "./components/FlowArrow";

interface Layer {
  label: string;
  sublabel: string;
  y: number;
  color: string;
  items: string[];
  delay: number;
}

const LAYERS: Layer[] = [
  {
    label: "Client Layer",
    sublabel: "Browser · AI IDEs · MCP Clients",
    y: 140,
    color: COLORS.layerClient,
    items: ["Next.js SSR", "Chat UI", "Admin Panel", "Claude / Cursor"],
    delay: 0,
  },
  {
    label: "Application Layer",
    sublabel: "API Routes · Auth · Rate Limiting",
    y: 310,
    color: COLORS.layerApp,
    items: ["REST API", "MCP Server", "Auth Middleware", "SSE Streaming"],
    delay: 20,
  },
  {
    label: "Core Logic",
    sublabel: "RAG Pipeline · Sync · Graph Engine",
    y: 480,
    color: COLORS.layerCore,
    items: ["Hybrid Search", "Reranker", "Entity Extraction", "Chunker"],
    delay: 40,
  },
  {
    label: "Data Layer",
    sublabel: "PostgreSQL · pgvector · Redis",
    y: 650,
    color: COLORS.layerData,
    items: ["Supabase DB", "Vector Store", "tsvector FTS", "Upstash Redis"],
    delay: 60,
  },
  {
    label: "External Services",
    sublabel: "LLM · Integrations · OCR",
    y: 820,
    color: COLORS.layerExternal,
    items: ["OpenRouter", "GitHub API", "Jira / Confluence", "Docling OCR"],
    delay: 80,
  },
];

const LAYER_WIDTH = 1400;
const LAYER_HEIGHT = 120;
const LAYER_X = 960;

export const Scene3Architecture: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Title
  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  // Exit
  const exitOpacity = interpolate(frame, [330, 360], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 35,
          left: "50%",
          transform: `translate(-50%, 0)`,
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
          fontSize: 38,
          fontFamily: FONTS.heading,
          fontWeight: 700,
          color: COLORS.textPrimary,
        }}
      >
        System Architecture —{" "}
        <span style={{ color: COLORS.accentPurple }}>5 Layers</span>
      </div>

      {/* Layers */}
      {LAYERS.map((layer, i) => {
        const layerAppear = spring({
          frame: frame - layer.delay - 15,
          fps,
          config: SPRING.bouncy,
        });

        const opacity = interpolate(layerAppear, [0, 1], [0, 1]);
        const translateX = interpolate(
          layerAppear,
          [0, 1],
          [i % 2 === 0 ? -100 : 100, 0],
        );

        return (
          <React.Fragment key={i}>
            {/* Layer box */}
            <div
              style={{
                position: "absolute",
                left: LAYER_X - LAYER_WIDTH / 2,
                top: layer.y - LAYER_HEIGHT / 2,
                width: LAYER_WIDTH,
                height: LAYER_HEIGHT,
                borderRadius: 16,
                background: `linear-gradient(135deg, ${layer.color}12, ${layer.color}06)`,
                border: `1px solid ${layer.color}40`,
                opacity,
                transform: `translateX(${translateX}px)`,
                display: "flex",
                alignItems: "center",
                padding: "0 40px",
                boxShadow: `0 0 30px ${layer.color}08`,
              }}
            >
              {/* Left: Layer name */}
              <div style={{ minWidth: 260 }}>
                <div
                  style={{
                    fontSize: 20,
                    fontFamily: FONTS.heading,
                    fontWeight: 700,
                    color: layer.color,
                  }}
                >
                  {layer.label}
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontFamily: FONTS.body,
                    color: COLORS.textMuted,
                    marginTop: 2,
                  }}
                >
                  {layer.sublabel}
                </div>
              </div>

              {/* Right: Items */}
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  marginLeft: "auto",
                }}
              >
                {layer.items.map((item, j) => {
                  const itemAppear = spring({
                    frame: frame - layer.delay - 25 - j * 6,
                    fps,
                    config: SPRING.snappy,
                  });

                  return (
                    <div
                      key={j}
                      style={{
                        padding: "8px 18px",
                        borderRadius: 8,
                        background: `${layer.color}15`,
                        border: `1px solid ${layer.color}30`,
                        fontFamily: FONTS.mono,
                        fontSize: 13,
                        color: COLORS.textSecondary,
                        opacity: interpolate(itemAppear, [0, 1], [0, 1]),
                        transform: `scale(${interpolate(itemAppear, [0, 1], [0.7, 1])})`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Arrow to next layer */}
            {i < LAYERS.length - 1 && (
              <FlowArrow
                fromX={LAYER_X}
                fromY={layer.y + LAYER_HEIGHT / 2}
                toX={LAYER_X}
                toY={LAYERS[i + 1].y - LAYER_HEIGHT / 2}
                delay={layer.delay + 30}
                color={layer.color}
                strokeWidth={1.5}
                dashed
              />
            )}
          </React.Fragment>
        );
      })}

      {/* RLS Security badge */}
      {(() => {
        const badgeAppear = spring({
          frame: frame - 120,
          fps,
          config: SPRING.snappy,
        });
        return (
          <div
            style={{
              position: "absolute",
              right: 80,
              top: 400,
              opacity: interpolate(badgeAppear, [0, 1], [0, 1]),
              transform: `scale(${interpolate(badgeAppear, [0, 1], [0.5, 1])})`,
              padding: "12px 20px",
              borderRadius: 12,
              background: `${COLORS.accentGreen}15`,
              border: `1px solid ${COLORS.accentGreen}40`,
              fontFamily: FONTS.mono,
              fontSize: 14,
              color: COLORS.accentGreen,
              textAlign: "center",
            }}
          >
            🔒 RLS Isolation
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
              tenant_id on every row
            </div>
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
